<script setup lang="ts">
  import { Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import TouristsRoomsDropdown from './TouristsRoomsDropdown.vue';
  import { normalizeTourists } from '../lib/touristsRooms';
  import { useTouristsRooms } from '../model/useTouristsRooms';
  import type { TouristsSelection } from '@/entities/searchCriteria';
  import { Field } from '@/entities/searchForm';
  import { useOutsidePointer } from '@/shared/composables';
  import { FieldSize } from '@/entities/searchForm';

  interface TouristsRoomsFieldProps {
    modelValue?: TouristsSelection | null;
    isVisibleAddButton?: boolean;
    size?: `${FieldSize}`;
  }

  const props = withDefaults(defineProps<TouristsRoomsFieldProps>(), {
    modelValue: null,
    isVisibleAddButton: true,
    size: FieldSize.M
  });

  interface TouristsRoomsFieldEmits {
    (e: 'update:modelValue', sel: TouristsSelection): void;
  }

  const emit = defineEmits<TouristsRoomsFieldEmits>();

  const model = computed<TouristsSelection>({
    get: () => normalizeTourists(props.modelValue),
    set: (value) => emit('update:modelValue', value),
  });

  const core = useTouristsRooms({ model, isVisibleAddButton: props.isVisibleAddButton });

  const rootRef = ref<HTMLElement | null>(null);

  const onToggle = (): void => {
    core.toggle();
  };

  useOutsidePointer(rootRef, { active: core.controlActivated, handler: core.close });

  defineExpose({
    validate: (): boolean => core.validate(),
  });
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    ref="rootRef"
    class="tourists-rooms-field"
    data-testid="tourists-rooms-field"
  >
    <TouristsRoomsDropdown
      :rooms="core.rooms.value"
      :visible="core.controlActivated.value"
      :age-options="core.ageOptions"
      :can-add-room="core.canAddRoom.value"
      :can-remove-room="core.canRemoveRoom.value"
      :is-visible-header="isVisibleAddButton"
      :is-visible-add-button="isVisibleAddButton"
      @set-adults="core.setAdults($event.roomIndex, $event.value)"
      @add-child="core.addChild($event.roomIndex, $event.age)"
      @change-child-age="core.setChildAge($event.roomIndex, $event.childIndex, $event.age)"
      @remove-child="core.removeChild($event.roomIndex, $event.childIndex)"
      @add-room="core.addRoom"
      @remove-room="core.removeRoom"
      @close="core.close"
    >
      <template #reference>
        <Field
          :label="core.roomsLabel.value"
          :active="core.controlActivated.value"
          :size="size"
          value-testid="tourists-rooms-value"
          @click="onToggle"
        >
          <template #value>
            <Typo
              class="tourists-rooms-field__typo"
              :variant="TypoVariant.BodyLRegular"
              tag="span"
            >
              {{ core.touristsValue.value }}
            </Typo>
          </template>
        </Field>
      </template>
    </TouristsRoomsDropdown>
  </div>
</template>

<style scoped lang="scss">
  .tourists-rooms-field {
    position: relative;
    width: 100%;

    &__typo {
      -webkit-box-orient: vertical;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
</style>
